import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  MenuItem,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Add as AddIcon,
  AutoAwesome as AiIcon,
  CloudOutlined as ProviderIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  PeopleAltOutlined as AccountsIcon,
  BarChartOutlined as StatsIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  SettingsOutlined as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WallpaperOutlined as PresetsIcon,
} from '@mui/icons-material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { toast } from 'react-toastify';
import UserAiThemes from '../User/UserAiThemes';
import {
  createBackgroundAiPreset,
  deleteBackgroundAiPreset,
  getBackgroundAiAdminPresets,
  getBackgroundAiAdminUsers,
  getBackgroundAiAdminAllUsageCosts,
  getBackgroundAiAdminUsage,
  getBackgroundAiAdminUsageCosts,
  getBackgroundAiAdminUsagePage,
  getBackgroundAiAdminUsageSubjects,
  BackgroundAiUsageSubjectPoint,
  BackgroundAiUsageMoneyPoint,
  purgeBackgroundAiAdminUsage,
  toggleBackgroundAiMultiSlot,
  toggleBackgroundAiUser,
  updateBackgroundAiPreset,
  updateBackgroundAiAdminProvider,
  getBackgroundAiAdminCatalog,
  getFalAccountBalance,
  updateBackgroundAiAccountGeneration,
  revealBackgroundAiAdminKeys,
  BackgroundAiPreset,
  BackgroundAiAdminUser,
  BackgroundAiUsageGeneration,
  BackgroundAiUsageMachine,
  BackgroundAiProviderId,
  BackgroundAiProviderState,
  BackgroundAiQuality,
} from '../../services/api';
import { rewriteToCdn } from '../../utils/cdn';
import { useI18n } from '../../i18n/useI18n';
import type { AppLang } from '../../i18n';

const REVEAL_MS = 5 * 60 * 1000;
const dotsFromLength = (n?: number) => (n && n > 0 ? '.'.repeat(n) : '');

function usageHistoryName(
  labels: { vi?: string; en?: string; ko?: string; zh?: string } | undefined,
  fallback: string | undefined,
  lang: AppLang,
) {
  return String(labels?.[lang] || '').trim() || String(fallback || '').trim();
}

const accountHue = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
};

const pinkFill = 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)';
const pinkHover = 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)';

const dialogPaperSx = {
  borderRadius: 3,
  overflow: 'hidden',
  border: '1px solid transparent',
  background:
    'linear-gradient(165deg, #ffffff 0%, #fff8fb 72%, #fff5f8 100%) padding-box, linear-gradient(135deg, rgba(255,139,167,0.95), rgba(244,143,177,0.55)) border-box',
  boxShadow: '0 24px 60px rgba(191, 95, 130, 0.18)',
};

const dialogTitleSx = {
  fontWeight: 800,
  color: '#bf5f82',
  background: 'linear-gradient(135deg, #fff5f8 0%, #ffeef3 100%)',
  borderBottom: '1px solid',
  borderColor: alpha('#ff8ba7', 0.45),
};

const dialogActionsSx = {
  px: 2.5,
  py: 1.5,
  justifyContent: 'flex-end',
  borderTop: '1px solid',
  borderColor: alpha('#f48fb1', 0.28),
  background: 'linear-gradient(180deg, #ffffff 0%, #fff8fb 100%)',
};

const dialogCancelSx = { color: '#e06a88', fontWeight: 700, textTransform: 'none' as const, borderRadius: 999 };

const dialogSaveSx = {
  fontWeight: 800,
  color: '#fff',
  borderRadius: 999,
  textTransform: 'none' as const,
  background: pinkFill,
  boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
  '&:hover': {
    background: pinkHover,
  },
  '&.Mui-disabled': { color: '#fff', background: alpha('#ff8ba7', 0.45), boxShadow: 'none' },
};

const settingsCardSx = {
  p: 1.75,
  borderRadius: 2.5,
  bgcolor: '#fff',
  border: '1px solid',
  borderColor: alpha('#ff8ba7', 0.45),
  boxShadow: '0 8px 18px rgba(191, 95, 130, 0.06)',
};

const settingsFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#ff8ba7', 0.55) },
    '&:hover fieldset': { borderColor: '#ff8ba7' },
    '&.Mui-focused fieldset': { borderColor: '#f48fb1' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#bf5f82' },
};

const pillToggleSx = {
  bgcolor: '#fff0f4',
  p: 0.4,
  borderRadius: 2,
  width: '100%',
  '& .MuiToggleButtonGroup-grouped': { flex: 1 },
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '10px !important',
    px: 1.5,
    py: 0.75,
    textTransform: 'none',
    fontWeight: 800,
    color: alpha('#bf5f82', 0.55),
    '&.Mui-selected': {
      bgcolor: '#fff',
      color: '#bf5f82',
      boxShadow: '0 1px 4px rgba(191, 95, 130, 0.16)',
    },
    '&.Mui-selected:hover': { bgcolor: '#fff' },
  },
};

const pinkSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundImage: 'linear-gradient(90deg, #ffc1d4, #ff8ba7)',
    opacity: 1,
  },
};

const chartFilterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#f0c9d6' },
    '&:hover fieldset': { borderColor: '#e7a8be' },
    '&.Mui-focused fieldset': { borderColor: '#e06a88' },
  },
  '& .MuiInputLabel-root': { color: '#bf5f82', fontWeight: 700 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#e06a88' },
  '& .MuiOutlinedInput-input': { color: '#5c3344' },
};

const gradientPanelSx = {
  p: { xs: 2, sm: 3 },
  borderRadius: 3,
  border: '1px solid transparent',
  background:
    'linear-gradient(165deg, #ffffff 0%, #fff8fb 58%, #fff5f8 100%) padding-box, linear-gradient(135deg, rgba(255,139,167,0.95), rgba(244,143,177,0.55)) border-box',
  boxShadow: '0 14px 36px rgba(191, 95, 130, 0.08)',
};

const accountActionButtonSx = (compact: boolean): SxProps<Theme> => ({
  borderRadius: 2.5,
  fontWeight: 700,
  whiteSpace: compact ? 'normal' : 'nowrap',
  lineHeight: 1.3,
  textAlign: 'center',
  px: 1.25,
  py: compact ? 1 : 0.6,
  width: compact ? '100%' : 'auto',
  minWidth: compact ? 0 : 'auto',
  height: compact ? '100%' : 'auto',
  '&.MuiButton-outlined': {
    color: '#bf5f82',
    border: '1px solid transparent',
    background:
      'linear-gradient(180deg, #ffffff 0%, #fff7fb 100%) padding-box, linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%) border-box',
    boxShadow: '0 4px 12px rgba(244, 143, 177, 0.16)',
  },
  '&.MuiButton-outlined:hover': {
    color: '#fff',
    border: '1px solid transparent',
    background: pinkFill,
    boxShadow: '0 8px 18px rgba(191, 95, 130, 0.24)',
  },
});

const SectionIcon = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      bgcolor: alpha('#ff8ba7', 0.16),
      color: '#bf5f82',
    }}
  >
    {children}
  </Box>
);

type UsageCostPoint = { t: number; userId: string; name: string; avatarUrl?: string; costUsd: number };
type UsageChartPart = { userId: string; name: string; avatarUrl: string; cost: number };

const USAGE_STACK_COLORS = [
  '#f48fb1',
  '#7e57c2',
  '#26a69a',
  '#42a5f5',
  '#ffb74d',
  '#ef5350',
  '#66bb6a',
  '#8d6e63',
  '#5c6bc0',
  '#ec407a',
  '#26c6da',
  '#ab47bc',
];
type UsageChartRange = '2m' | '1m' | '2w' | '1w' | '7d' | '2d' | '1d';

const USAGE_CHART_RANGES: { id: UsageChartRange; labelKey: 'usageChart2m' | 'usageChart1m' | 'usageChart1w' | 'usageChart7d' | 'usageChart2d' | 'usageChart1d' }[] = [
  { id: '2m', labelKey: 'usageChart2m' },
  { id: '1m', labelKey: 'usageChart1m' },
  { id: '1w', labelKey: 'usageChart1w' },
  { id: '7d', labelKey: 'usageChart7d' },
  { id: '2d', labelKey: 'usageChart2d' },
  { id: '1d', labelKey: 'usageChart1d' },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const CHART_MAX_DAYS = 62;

const startOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const dateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateInput = (value: string) => {
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (!year || !month || !day) return startOfDay(new Date());
  return startOfDay(new Date(year, month - 1, day));
};

const chartPresetBounds = (range: UsageChartRange, now = new Date()) => {
  const today = startOfDay(now);
  let from = today;
  if (range === '2d') from = new Date(today.getTime() - DAY_MS);
  else if (range === '7d') from = new Date(today.getTime() - 6 * DAY_MS);
  else if (range === '1w') {
    const weekday = today.getDay();
    const mondayOffset = weekday === 0 ? 6 : weekday - 1;
    from = new Date(today.getTime() - mondayOffset * DAY_MS);
  } else if (range === '2w') from = new Date(today.getTime() - 13 * DAY_MS);
  else if (range === '1m') from = new Date(today.getTime() - 29 * DAY_MS);
  else if (range === '2m') from = new Date(today.getTime() - (CHART_MAX_DAYS - 1) * DAY_MS);
  return { from: dateInputValue(from), to: dateInputValue(today) };
};

const usageChartBuckets = (fromValue: string, toValue: string, points: UsageCostPoint[], now = Date.now()) => {
  const current = new Date(now);
  const today = startOfDay(current);
  const from = parseDateInput(fromValue);
  const to = parseDateInput(toValue);
  const hourly = from.getTime() === to.getTime();
  const step = hourly ? 60 * 60 * 1000 : DAY_MS;
  const count = hourly
    ? (from.getTime() === today.getTime() ? current.getHours() + 1 : 24)
    : Math.round((to.getTime() - from.getTime()) / DAY_MS) + 1;
  const start = from;
  const buckets = Array.from({ length: count }, (_, index) => ({
    t: start.getTime() + index * step,
    cost: 0,
    parts: [] as UsageChartPart[],
  }));
  const end = start.getTime() + count * step;
  for (const point of points) {
    if (point.t < start.getTime() || point.t >= end) continue;
    const index = Math.floor((point.t - start.getTime()) / step);
    const bucket = buckets[index];
    if (!bucket) continue;
    bucket.cost += point.costUsd;
    const part = bucket.parts.find((item) => item.userId === point.userId);
    if (part) part.cost += point.costUsd;
    else bucket.parts.push({
      userId: point.userId,
      name: point.name || point.userId,
      avatarUrl: point.avatarUrl || '',
      cost: point.costUsd,
    });
  }
  const accounts = new Map<string, { name: string; avatarUrl: string }>();
  for (const bucket of buckets) {
    bucket.parts.sort((a, b) => a.name.localeCompare(b.name));
    for (const part of bucket.parts) accounts.set(part.userId, { name: part.name, avatarUrl: part.avatarUrl });
  }
  const accountList: { userId: string; name: string; avatarUrl: string }[] = [];
  accounts.forEach((info, userId) => accountList.push({ userId, name: info.name, avatarUrl: info.avatarUrl }));
  accountList.sort((a, b) => a.name.localeCompare(b.name));
  return { buckets, hourly, accounts: accountList };
};

const usageStackColor = (userId: string, accounts: { userId: string }[]) => {
  const index = Math.max(0, accounts.findIndex((item) => item.userId === userId));
  return USAGE_STACK_COLORS[index % USAGE_STACK_COLORS.length];
};

const usageChartLabel = (time: number, hourly: boolean) => {
  const date = new Date(time);
  if (hourly) return `${date.getHours()}h`;
  return `${date.getDate()}/${date.getMonth() + 1}`;
};

const BackgroundAi: React.FC = () => {
  const { admin, formatMessage, lang } = useI18n();
  const b = admin.backgroundAi;
  const theme = useTheme();
  const compactAccounts = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [presets, setPresets] = useState<BackgroundAiPreset[]>([]);
  const [configured, setConfigured] = useState(true);
  const [providerState, setProviderState] = useState<BackgroundAiProviderState | null>(null);
  const [providerId, setProviderId] = useState<BackgroundAiProviderId>('fal');
  const [quality, setQuality] = useState<BackgroundAiQuality>('low');
  const [piapiApiKey, setPiapiApiKey] = useState('');
  const [falApiKey, setFalApiKey] = useState('');
  const [piapiKeyDirty, setPiapiKeyDirty] = useState(false);
  const [falKeyDirty, setFalKeyDirty] = useState(false);
  const [revealedPiapiKey, setRevealedPiapiKey] = useState('');
  const [revealedFalKey, setRevealedFalKey] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [typedKeyVisible, setTypedKeyVisible] = useState(false);
  const [revealExpiresAt, setRevealExpiresAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [falBalance, setFalBalance] = useState<string>('');
  const [falBalanceError, setFalBalanceError] = useState('');
  const [loadingFalBalance, setLoadingFalBalance] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState<'provider' | 'accounts' | null>('accounts');
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsRange, setStatsRange] = useState<UsageChartRange>('7d');
  const togglePanel = (panel: 'provider' | 'accounts') => {
    setOpenPanel((current) => (current === panel ? null : panel));
  };
  const [loadingPresets, setLoadingPresets] = useState(true);
  const [users, setUsers] = useState<BackgroundAiAdminUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usageCostPoints, setUsageCostPoints] = useState<UsageCostPoint[] | null>(null);
  const [allUsageCostLoading, setAllUsageCostLoading] = useState(false);
  const [statsCostFailed, setStatsCostFailed] = useState(false);
  const [chartAccountIds, setChartAccountIds] = useState<string[]>([]);
  const [chartFocus, setChartFocus] = useState<number | null>(null);
  const [togglingUserId, setTogglingUserId] = useState('');
  const [usageUser, setUsageUser] = useState<BackgroundAiAdminUser | null>(null);
  const [settingsUser, setSettingsUser] = useState<BackgroundAiAdminUser | null>(null);
  const [themeUser, setThemeUser] = useState<BackgroundAiAdminUser | null>(null);
  const [themeDetailOpen, setThemeDetailOpen] = useState(false);
  const [savingMultiSlot, setSavingMultiSlot] = useState(false);
  const [accountProvider, setAccountProvider] = useState<BackgroundAiProviderId>('fal');
  const [accountQuality, setAccountQuality] = useState<BackgroundAiQuality>('low');
  const [accountModel, setAccountModel] = useState('');
  const [accountQuota, setAccountQuota] = useState('');
  const [accountModels, setAccountModels] = useState<Array<{ id: string; name: string; priceLabel?: string }>>([]);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [savingAccountGen, setSavingAccountGen] = useState(false);
  const accountInitRef = useRef('');
  const [multiSlotConfirmOpen, setMultiSlotConfirmOpen] = useState(false);
  const [usageMachines, setUsageMachines] = useState<BackgroundAiUsageMachine[]>([]);
  const [openUsageCode, setOpenUsageCode] = useState<string | null>(null);
  const [usageSearch, setUsageSearch] = useState('');
  const [usageProduct, setUsageProduct] = useState<'all' | 'lite' | 'pro'>('all');
  const [usagePages, setUsagePages] = useState<
    Record<string, { items: BackgroundAiUsageGeneration[]; loading: boolean }>
  >({});
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageCostsLoading, setUsageCostsLoading] = useState(false);
  const [usageRequestCosts, setUsageRequestCosts] = useState<Record<string, number>>({});
  const [accountMoneyPoints, setAccountMoneyPoints] = useState<BackgroundAiUsageMoneyPoint[] | null>(null);
  const [subjectOpen, setSubjectOpen] = useState(false);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [subjectPoints, setSubjectPoints] = useState<BackgroundAiUsageSubjectPoint[] | null>(null);
  const [subjectUserId, setSubjectUserId] = useState('');
  const [subjectTab, setSubjectTab] = useState<'usage' | 'money'>('usage');
  const [subjectKind, setSubjectKind] = useState<'theme' | 'preset'>('theme');
  const [subjectRange, setSubjectRange] = useState<UsageChartRange | null>('7d');
  const [subjectFrom, setSubjectFrom] = useState(() => chartPresetBounds('7d').from);
  const [subjectTo, setSubjectTo] = useState(() => chartPresetBounds('7d').to);
  const [subjectFocus, setSubjectFocus] = useState<number | null>(null);
  const subjectScrollRef = useRef<HTMLDivElement | null>(null);
  const moneyScrollRef = useRef<HTMLDivElement | null>(null);
  const usageRequestRef = useRef(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<BackgroundAiPreset | null>(null);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const loadPresets = useCallback(async () => {
    try {
      setLoadingPresets(true);
      const data = await getBackgroundAiAdminPresets();
      setPresets(data.presets || []);
      setConfigured(data.configured !== false);
      if (data.provider) {
        setProviderState(data.provider);
        setProviderId(data.provider.provider || 'fal');
        setQuality(data.provider.quality || 'low');
        const current =
          (data.provider.provider === 'fal'
            ? data.provider.fal?.model
            : data.provider.piapi?.model) || '';
        setSelectedModel(current);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastLoadPresetsFail);
    } finally {
      setLoadingPresets(false);
    }
  }, []);

  const loadUsers = useCallback(async (search = userSearch) => {
    try {
      setLoadingUsers(true);
      const data = await getBackgroundAiAdminUsers(1, 50, search);
      setUsers(data.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastLoadUsersFail);
    } finally {
      setLoadingUsers(false);
    }
  }, [userSearch]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    void purgeBackgroundAiAdminUsage().catch(() => {});
  }, []);

  const statsCostLoadingRef = useRef(false);
  useEffect(() => {
    if (!statsOpen || usageCostPoints != null || statsCostLoadingRef.current) return;
    statsCostLoadingRef.current = true;
    setAllUsageCostLoading(true);
    setStatsCostFailed(false);
    void getBackgroundAiAdminAllUsageCosts()
      .then((result) => {
        setUsageCostPoints(Array.isArray(result.points) ? result.points : []);
      })
      .catch(() => {
        setStatsCostFailed(true);
      })
      .finally(() => {
        statsCostLoadingRef.current = false;
        setAllUsageCostLoading(false);
      });
  }, [statsOpen, usageCostPoints]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const revealUnlocked = revealExpiresAt != null && nowTick < revealExpiresAt;

  useEffect(() => {
    if (!revealExpiresAt) return;
    if (nowTick < revealExpiresAt) return;
    setRevealExpiresAt(null);
    setRevealedPiapiKey('');
    setRevealedFalKey('');
    setKeyVisible(false);
  }, [revealExpiresAt, nowTick]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadUsers(userSearch);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadUsers, userSearch]);

  const resetDialog = () => {
    setEditingPreset(null);
    setName('');
    setPrompt('');
    setImageFile(null);
    setImagePreview('');
  };

  const openCreateDialog = () => {
    resetDialog();
    setDialogOpen(true);
  };

  const openEditDialog = (preset: BackgroundAiPreset) => {
    setEditingPreset(preset);
    setName(preset.name || '');
    setPrompt(preset.prompt || '');
    setImageFile(null);
    setImagePreview(preset.imageUrl ? rewriteToCdn(preset.imageUrl) : '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !prompt.trim()) {
      toast.error(b.toastNamePromptRequired);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        prompt: prompt.trim(),
        image: imageFile || undefined,
      };
      if (editingPreset) {
        await updateBackgroundAiPreset(editingPreset.id, payload);
        toast.success(b.toastPresetSaved);
      } else {
        await createBackgroundAiPreset(payload);
        toast.success(b.toastPresetCreated);
      }
      setDialogOpen(false);
      resetDialog();
      await loadPresets();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastPresetSaveFail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (preset: BackgroundAiPreset) => {
    if (!window.confirm(formatMessage(b.toastDeleteConfirm, { name: preset.name }))) return;
    try {
      setDeletingId(preset.id);
      await deleteBackgroundAiPreset(preset.id);
      toast.success(b.toastPresetDeleted);
      await loadPresets();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastPresetDeleteFail);
    } finally {
      setDeletingId('');
    }
  };

  const qualityLabel = (quality: string) => {
    if (quality === 'medium') return b.qualityMedium;
    if (quality === 'high' || quality === 'xhigh' || quality === 'max') return b.qualityHigh;
    if (quality === 'low') return b.qualityLow;
    return quality || b.qualityLow;
  };

  const openUsage = async (user: BackgroundAiAdminUser) => {
    const ticket = usageRequestRef.current + 1;
    usageRequestRef.current = ticket;
    setUsageUser(user);
    setUsageMachines([]);
    setOpenUsageCode(null);
    setUsagePages({});
    setUsageSearch('');
    setUsageProduct('all');
    setUsageCostsLoading(false);
    setUsageRequestCosts({});
    setAccountMoneyPoints(null);
    setSubjectTab('usage');
    setSubjectOpen(false);
    setSubjectPoints(null);
    setSubjectUserId('');
    setLoadingUsage(true);
    try {
      const result = await getBackgroundAiAdminUsage(user._id);
      if (ticket !== usageRequestRef.current) return;
      setUsageMachines(result.machines || []);
      setUsageCostsLoading(true);
      void getBackgroundAiAdminUsageCosts(user._id)
        .then((costs) => {
          if (ticket !== usageRequestRef.current) return;
          const byCode = new Map(
            (costs.machines || []).map((row) => [String(row.machineCode || '').toUpperCase(), row.costUsd]),
          );
          setUsageMachines((prev) => prev.map((machine) => {
            const cost = byCode.get(String(machine.machineCode || '').toUpperCase());
            return cost == null ? machine : { ...machine, costUsd: cost };
          }));
          setUsageRequestCosts(costs.requestCosts || {});
          setAccountMoneyPoints(costs.points || []);
        })
        .catch(() => {})
        .finally(() => {
          if (ticket === usageRequestRef.current) setUsageCostsLoading(false);
        });
    } catch (error: any) {
      if (ticket !== usageRequestRef.current) return;
      toast.error(error?.response?.data?.error || b.usageLoadFail);
    } finally {
      if (ticket === usageRequestRef.current) setLoadingUsage(false);
    }
  };

  const loadUsagePage = async (userId: string, machineCode: string, skip: number) => {
    const ticket = usageRequestRef.current;
    setUsagePages((prev) => ({
      ...prev,
      [machineCode]: {
        items: skip > 0 ? prev[machineCode]?.items || [] : [],
        loading: true,
      },
    }));
    try {
      const result = await getBackgroundAiAdminUsagePage(userId, machineCode, skip);
      if (ticket !== usageRequestRef.current) return;
      setUsagePages((prev) => {
        const prevItems = skip > 0 ? prev[machineCode]?.items || [] : [];
        return {
          ...prev,
          [machineCode]: {
            items: [...prevItems, ...(result.generations || [])],
            loading: false,
          },
        };
      });
    } catch (error: any) {
      if (ticket !== usageRequestRef.current) return;
      setUsagePages((prev) => ({
        ...prev,
        [machineCode]: {
          items: prev[machineCode]?.items || [],
          loading: false,
        },
      }));
      toast.error(error?.response?.data?.error || b.usageLoadFail);
    }
  };

  useEffect(() => {
    if (!settingsUser) {
      accountInitRef.current = '';
      return;
    }
    if (accountInitRef.current === settingsUser._id) return;
    accountInitRef.current = settingsUser._id;
    const nextProvider = (settingsUser.backgroundAiProvider || providerId) as BackgroundAiProviderId;
    const rawQuality = (settingsUser.backgroundAiQuality || quality || 'low') as BackgroundAiQuality;
    const nextQuality: BackgroundAiQuality = rawQuality === 'auto' ? 'low' : rawQuality;
    const savedModel =
      nextProvider === 'fal' ? settingsUser.backgroundAiFalModel : settingsUser.backgroundAiPiapiModel;
    const fallbackModel = nextProvider === providerId ? selectedModel : '';
    setAccountProvider(nextProvider);
    setAccountQuality(nextQuality);
    setAccountModel(savedModel || fallbackModel || '');
    setAccountQuota(
      settingsUser.quotaLimit == null ? '' : String(settingsUser.quotaRemaining ?? 0),
    );
  }, [settingsUser, providerId, quality, selectedModel]);

  useEffect(() => {
    if (!settingsUser) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getBackgroundAiAdminCatalog(accountProvider, accountQuality);
        if (cancelled) return;
        const models = data.models || [];
        setAccountModels(models);
        setAccountModel((prev) => (models.some((model) => model.id === prev) ? prev : models[0]?.id || ''));
      } catch {
        if (!cancelled) setAccountModels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settingsUser, accountProvider, accountQuality]);

  const saveAccountGeneration = async () => {
    if (!settingsUser || !accountModel) return;
    const quotaText = accountQuota.trim();
    let quotaLimit: number | null = null;
    if (quotaText) {
      const parsed = Number(quotaText);
      if (!Number.isInteger(parsed) || parsed < 0) {
        toast.error(b.quotaHint);
        return;
      }
      quotaLimit = parsed;
    }
    try {
      setSavingAccountGen(true);
      const saved = await updateBackgroundAiAccountGeneration(settingsUser._id, {
        provider: accountProvider,
        quality: accountQuality,
        model: accountModel,
        quotaLimit,
      });
      const apply = (item: BackgroundAiAdminUser) =>
        item._id === settingsUser._id
          ? {
              ...item,
              backgroundAiProvider: saved.backgroundAiProvider,
              backgroundAiQuality: saved.backgroundAiQuality,
              backgroundAiFalModel: saved.backgroundAiFalModel,
              backgroundAiPiapiModel: saved.backgroundAiPiapiModel,
              quotaLimit: saved.quotaLimit,
              quotaUsed: saved.quotaUsed,
              quotaRemaining: saved.quotaRemaining,
            }
          : item;
      setUsers((prev) => prev.map(apply));
      setSettingsUser((prev) => (prev ? apply(prev) : prev));
      toast.success(b.accountGenSaved);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.accountGenFail);
    } finally {
      setSavingAccountGen(false);
    }
  };

  const handleToggleMultiSlot = async (user: BackgroundAiAdminUser) => {
    const next = user.backgroundAiMultiSlotEnabled !== true;
    if (next) {
      setMultiSlotConfirmOpen(true);
      return;
    }
    await saveMultiSlot(user, false);
  };

  const saveMultiSlot = async (user: BackgroundAiAdminUser, next: boolean) => {
    try {
      setSavingMultiSlot(true);
      await toggleBackgroundAiMultiSlot(user._id, next);
      const apply = (item: BackgroundAiAdminUser) =>
        item._id === user._id ? { ...item, backgroundAiMultiSlotEnabled: next } : item;
      setUsers((prev) => prev.map(apply));
      setSettingsUser((prev) => (prev && prev._id === user._id ? { ...prev, backgroundAiMultiSlotEnabled: next } : prev));
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastUserToggleFail);
    } finally {
      setSavingMultiSlot(false);
    }
  };

  const handleToggleUser = async (user: BackgroundAiAdminUser) => {
    try {
      setTogglingUserId(user._id);
      const next = !user.backgroundAiEnabled;
      await toggleBackgroundAiUser(user._id, next);
      setUsers((prev) =>
        prev.map((item) =>
          item._id === user._id ? { ...item, backgroundAiEnabled: next } : item
        )
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastUserToggleFail);
    } finally {
      setTogglingUserId('');
    }
  };

  const confirmRevealPassword = async () => {
    if (!pwInput.trim()) {
      toast.error(b.toastPasswordRequired);
      return;
    }
    try {
      setPwBusy(true);
      const data = await revealBackgroundAiAdminKeys(pwInput.trim());
      setRevealedPiapiKey(data.piapiApiKey || '');
      setRevealedFalKey(data.falApiKey || '');
      setRevealExpiresAt(Date.now() + (data.expiresInMs || REVEAL_MS));
      setKeyVisible(true);
      setPwDialogOpen(false);
      setPwInput('');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastPasswordWrong);
    } finally {
      setPwBusy(false);
    }
  };

  const handleEyeClick = (dirty: boolean) => {
    if (dirty) {
      setTypedKeyVisible((v) => !v);
      return;
    }
    if (revealUnlocked) {
      setKeyVisible((v) => !v);
      return;
    }
    setPwInput('');
    setPwDialogOpen(true);
  };

  const applyProviderState = (next: BackgroundAiProviderState, modelFallback = selectedModel) => {
    setProviderState(next);
    setProviderId(next.provider || providerId);
    setQuality(next.quality || quality);
    const current =
      (next.provider === 'fal' ? next.fal?.model : next.piapi?.model) || modelFallback;
    setSelectedModel(current);
    setConfigured(next.configured !== false);
  };

  const loadFalBalance = useCallback(async () => {
    try {
      setLoadingFalBalance(true);
      setFalBalanceError('');
      const data = await getFalAccountBalance();
      const amount = data.balance == null
        ? '—'
        : new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: data.currency || 'USD',
          }).format(data.balance);
      setFalBalance(amount);
    } catch (error: any) {
      setFalBalance('');
      setFalBalanceError(error?.response?.data?.error || b.toastProviderFail);
    } finally {
      setLoadingFalBalance(false);
    }
  }, [b.toastProviderFail]);

  useEffect(() => {
    void loadFalBalance();
  }, [loadFalBalance]);

  const handleSaveProvider = async () => {
    if (!piapiKeyDirty && !falKeyDirty) return;
    try {
      setSavingProvider(true);
      const next = await updateBackgroundAiAdminProvider({
        piapiApiKey: piapiKeyDirty ? piapiApiKey.trim() || undefined : undefined,
        falApiKey: falKeyDirty ? falApiKey.trim() || undefined : undefined,
      });
      applyProviderState(next);
      setPiapiApiKey('');
      setFalApiKey('');
      setPiapiKeyDirty(false);
      setFalKeyDirty(false);
      if (piapiKeyDirty && piapiApiKey.trim()) setRevealedPiapiKey(piapiApiKey.trim());
      if (falKeyDirty && falApiKey.trim()) setRevealedFalKey(falApiKey.trim());
      toast.success(b.toastProviderSwitched);
      void loadFalBalance();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || b.toastProviderFail);
    } finally {
      setSavingProvider(false);
    }
  };

  const visibleUsageMachines = usageMachines.filter((machine) => {
    const product = String(machine.appProduct || '').trim().toLowerCase();
    if (usageProduct !== 'all' && product !== usageProduct) return false;
    const query = usageSearch.trim().toLowerCase();
    if (!query) return true;
    const name = String(machine.machineName || '').toLowerCase();
    const code = String(machine.machineCode || '').toLowerCase();
    return name.includes(query) || code.includes(query);
  });
  const usageCostTotal = visibleUsageMachines.reduce((sum, machine) => sum + (Number(machine.costUsd) || 0), 0);
  const showUsageCost = visibleUsageMachines.some((machine) => machine.costUsd != null);
  const generationCost = (item: { costUsd?: number | null; requestIds?: string[] }) => {
    if (item.costUsd != null) return item.costUsd;
    const ids = item.requestIds || [];
    if (!ids.length || ids.some((id) => usageRequestCosts[id] == null)) return null;
    return Number(ids.reduce((sum, id) => sum + usageRequestCosts[id], 0).toFixed(4));
  };

  const chartAccountSet = new Set(chartAccountIds);
  const chartPoints = (usageCostPoints || []).filter((point) => chartAccountSet.size === 0 || chartAccountSet.has(point.userId));
  const chartBounds = chartPresetBounds(statsRange);
  const usageChart = usageCostPoints
    ? usageChartBuckets(chartBounds.from, chartBounds.to, chartPoints)
    : null;
  const usageChartPeak = Math.max(0, ...(usageChart?.buckets.map((bucket) => bucket.cost) || [0]));
  const usageChartTotal = usageChart?.buckets.reduce((sum, bucket) => sum + bucket.cost, 0) || 0;
  const usageChartCount = usageChart?.buckets.length || 0;
  const chartFocusBucket = usageChart?.buckets.find((bucket) => bucket.t === chartFocus) || null;
  const chartRows = (() => {
    const totals = new Map<string, { userId: string; name: string; avatarUrl: string; count: number }>();
    const parts = chartFocusBucket
      ? chartFocusBucket.parts
      : (usageChart?.buckets || []).flatMap((bucket) => bucket.parts);
    parts.forEach((part) => {
      const row = totals.get(part.userId) || { userId: part.userId, name: part.name, avatarUrl: part.avatarUrl, count: 0 };
      row.count += part.cost;
      row.name = part.name;
      row.avatarUrl = part.avatarUrl || row.avatarUrl;
      totals.set(part.userId, row);
    });
    return Array.from(totals.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  })();
  const chartRowMax = Math.max(1, ...chartRows.map((row) => row.count));
  const chartRowTotal = chartRows.reduce((sum, row) => sum + row.count, 0);
  const usageChartScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollUsageChartToToday = () => {
    const node = usageChartScrollRef.current;
    if (!node) return;
    node.scrollLeft = node.scrollWidth;
  };

  useEffect(() => {
    if (!statsOpen) return;
    const timers = [0, 50, 200, 400].map((delay) => window.setTimeout(scrollUsageChartToToday, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [usageCostPoints, usageChartCount, compactAccounts, statsOpen, chartAccountIds, allUsageCostLoading, statsRange]);

  const subjectChartPoints = (subjectPoints || []).filter((point) => {
    if (usageProduct === 'all') return true;
    const machine = usageMachines.find(
      (item) => String(item.machineCode || '').toUpperCase() === String(point.machineCode || '').toUpperCase(),
    );
    return String(machine?.appProduct || '').toLowerCase() === usageProduct;
  }).map((point) => {
    const labels = subjectKind === 'theme' ? point.themeLabels : point.presetLabels;
    const fallback = subjectKind === 'theme' ? point.themeName : point.presetName;
    const name = usageHistoryName(labels, fallback, lang) || b.usageSubjectUnknown;
    const id = subjectKind === 'theme' ? point.themeId : point.presetId;
    return {
      t: point.t,
      userId: id || `name:${name}`,
      name,
      costUsd: 1,
    };
  });
  const subjectChart = subjectOpen ? usageChartBuckets(subjectFrom, subjectTo, subjectChartPoints) : null;
  const subjectPeak = Math.max(0, ...(subjectChart?.buckets.map((bucket) => bucket.cost) || [0]));
  const subjectFocusBucket = subjectChart?.buckets.find((bucket) => bucket.t === subjectFocus) || null;
  const subjectRows = (() => {
    const totals = new Map<string, { userId: string; name: string; count: number }>();
    const parts = subjectFocusBucket
      ? subjectFocusBucket.parts
      : (subjectChart?.buckets || []).flatMap((bucket) => bucket.parts);
    parts.forEach((part) => {
      const row = totals.get(part.userId) || { userId: part.userId, name: part.name, count: 0 };
      row.count += part.cost;
      row.name = part.name;
      totals.set(part.userId, row);
    });
    return Array.from(totals.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  })();
  const subjectRowMax = Math.max(1, ...subjectRows.map((row) => row.count));
  const subjectRowTotal = subjectRows.reduce((sum, row) => sum + row.count, 0);
  const moneyPoints = (accountMoneyPoints || []).filter((point) => {
    if (usageProduct === 'all') return true;
    const machine = usageMachines.find(
      (item) => String(item.machineCode || '').toUpperCase() === String(point.machineCode || '').toUpperCase(),
    );
    return String(machine?.appProduct || '').toLowerCase() === usageProduct;
  }).map((point) => {
    const machine = usageMachines.find(
      (item) => String(item.machineCode || '').toUpperCase() === String(point.machineCode || '').toUpperCase(),
    );
    const name = machine?.machineName || point.machineCode;
    return { t: point.t, userId: point.machineCode, name, costUsd: point.costUsd };
  });
  const moneyChart = subjectOpen && subjectTab === 'money' ? usageChartBuckets(subjectFrom, subjectTo, moneyPoints) : null;
  const moneyPeak = Math.max(0, ...(moneyChart?.buckets.map((bucket) => bucket.cost) || [0]));
  const moneyCount = moneyChart?.buckets.length || 0;
  const moneyFocusBucket = moneyChart?.buckets.find((bucket) => bucket.t === subjectFocus) || null;
  const moneyRows = (() => {
    const totals = new Map<string, { userId: string; name: string; count: number }>();
    const parts = moneyFocusBucket
      ? moneyFocusBucket.parts
      : (moneyChart?.buckets || []).flatMap((bucket) => bucket.parts);
    parts.forEach((part) => {
      const row = totals.get(part.userId) || { userId: part.userId, name: part.name, count: 0 };
      row.count += part.cost;
      row.name = part.name;
      totals.set(part.userId, row);
    });
    return Array.from(totals.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  })();
  const moneyRowMax = Math.max(1, ...moneyRows.map((row) => row.count));
  const moneyRowTotal = moneyRows.reduce((sum, row) => sum + row.count, 0);

  useEffect(() => {
    const node = subjectScrollRef.current;
    if (!node) return;
    node.scrollLeft = node.scrollWidth;
  }, [subjectOpen, subjectKind, subjectRange, subjectFrom, subjectTo, usageProduct, subjectPoints, subjectChart?.buckets.length]);

  useEffect(() => {
    const node = moneyScrollRef.current;
    if (!node) return;
    node.scrollLeft = node.scrollWidth;
  }, [subjectOpen, subjectTab, subjectRange, subjectFrom, subjectTo, usageProduct, moneyCount, accountMoneyPoints]);

  const openSubjectStats = () => {
    if (!usageUser) return;
    setSubjectOpen(true);
    if (subjectUserId === usageUser._id && subjectPoints) return;
    setSubjectLoading(true);
    setSubjectUserId(usageUser._id);
    void getBackgroundAiAdminUsageSubjects(usageUser._id)
      .then((result) => {
        setSubjectPoints(result.points || []);
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.error || b.usageLoadFail);
        setSubjectPoints([]);
      })
      .finally(() => setSubjectLoading(false));
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, sm: 3 },
        ...(openPanel === 'accounts'
          ? {
              height: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
              pb: { xs: 1.5, sm: 2 },
            }
          : {}),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <AiIcon sx={{ color: '#ff8ba7' }} />
        <Typography variant="h4" fontWeight={700}>
          {b.title}
        </Typography>
      </Stack>

      <Paper
        sx={{
          ...gradientPanelSx,
          mb: 3,
        }}
      >
        <Box
          onClick={() => togglePanel('provider')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: '#fff',
              background: 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)',
              boxShadow: '0 6px 14px rgba(191, 95, 130, 0.28)',
            }}
          >
            <ProviderIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {b.providerTitle}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              color: '#e06a88',
              transition: 'transform 0.2s',
              transform: openPanel === 'provider' ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
        <Collapse in={openPanel === 'provider'}>
        <Stack spacing={2} sx={{ mt: 2.5 }}>
          <ToggleButtonGroup
            exclusive
            value={providerId}
            onChange={(_, value) => {
              if (!value) return;
              (document.activeElement as HTMLElement | null)?.blur();
              setProviderId(value);
              setTypedKeyVisible(false);
            }}
            sx={{
              alignSelf: 'flex-start',
              bgcolor: '#fff0f4',
              p: 0.4,
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                border: 0,
                borderRadius: '10px !important',
                px: 2.2,
                py: 0.7,
                textTransform: 'none',
                fontWeight: 700,
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: '#fff',
                  color: '#bf5f82',
                  boxShadow: '0 1px 4px rgba(191, 95, 130,0.16)',
                },
              },
            }}
          >
            <ToggleButton value="fal">Fal</ToggleButton>
            <ToggleButton value="piapi">PiAPI</ToggleButton>
          </ToggleButtonGroup>
          {providerId === 'fal' && (
            <Chip
              label={loadingFalBalance ? b.falBalanceLoading : falBalanceError || formatMessage(b.falBalance, { amount: falBalance || '—' })}
              sx={{
                alignSelf: 'flex-start',
                height: 'auto',
                py: 0.75,
                fontWeight: 800,
                color: falBalanceError ? '#b71c1c' : '#bf5f82',
                bgcolor: falBalanceError ? alpha('#e53935', 0.08) : alpha('#ff8ba7', 0.16),
                '& .MuiChip-label': { whiteSpace: 'normal' },
              }}
            />
          )}
          {(['fal', 'piapi'] as BackgroundAiProviderId[]).map((which) => {
            const dirty = which === 'piapi' ? piapiKeyDirty : falKeyDirty;
            const typed = which === 'piapi' ? piapiApiKey : falApiKey;
            const revealed = which === 'piapi' ? revealedPiapiKey : revealedFalKey;
            const meta = which === 'piapi' ? providerState?.piapi : providerState?.fal;
            const length = meta?.apiKeyLength || 0;
            const mask = dotsFromLength(length);
            const showingPlain = !dirty && keyVisible && revealUnlocked;
            const value = dirty ? typed : showingPlain ? revealed : mask;
            return (
              <TextField
                key={which}
                label={which === 'piapi' ? b.piapiKey : b.falKey}
                value={value}
                fullWidth
                type={dirty && !typedKeyVisible ? 'password' : 'text'}
                autoComplete="new-password"
                sx={{ display: providerId === which ? 'block' : 'none' }}
                onFocus={() => {
                  if (dirty || showingPlain) return;
                  setTypedKeyVisible(false);
                  if (which === 'piapi') {
                    setPiapiKeyDirty(true);
                    setPiapiApiKey('');
                  } else {
                    setFalKeyDirty(true);
                    setFalApiKey('');
                  }
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  if (!text) return;
                  e.preventDefault();
                  setTypedKeyVisible(false);
                  if (which === 'piapi') {
                    setPiapiKeyDirty(true);
                    setPiapiApiKey(text.trim());
                  } else {
                    setFalKeyDirty(true);
                    setFalApiKey(text.trim());
                  }
                }}
                onChange={(e) => {
                  const next = e.target.value;
                  if (which === 'piapi') {
                    setPiapiKeyDirty(true);
                    setPiapiApiKey(next);
                  } else {
                    setFalKeyDirty(true);
                    setFalApiKey(next);
                  }
                }}
                inputProps={{
                  spellCheck: false,
                  autoComplete: 'new-password',
                  name: `mphoto-bgai-${which}-key`,
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleEyeClick(dirty)}
                        disabled={!dirty && !length}
                        tabIndex={-1}
                      >
                        {(dirty ? typedKeyVisible : keyVisible && revealUnlocked)
                          ? <VisibilityOffIcon fontSize="small" />
                          : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  !length
                    ? formatMessage(b.noKey, { name: which === 'piapi' ? 'PiAPI' : 'Fal' })
                    : revealUnlocked
                      ? formatMessage(b.revealCountdown, {
                          s: Math.max(0, Math.ceil(((revealExpiresAt || 0) - nowTick) / 1000)),
                        })
                      : undefined
                }
              />
            );
          })}
          <Box>
            <Button
              variant="contained"
              onClick={handleSaveProvider}
              disabled={savingProvider}
              sx={{
                color: '#fff',
                background: 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)',
                boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)',
                },
              }}
            >
              {savingProvider ? b.saving : b.saveProvider}
            </Button>
          </Box>
        </Stack>
        </Collapse>
      </Paper>

      {!configured && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {b.missingKey}
        </Alert>
      )}

      {false && <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha('#ff8ba7', 0.12),
          boxShadow: '0 8px 28px rgba(191, 95, 130, 0.06)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1.5}
        >
          <Box
            onClick={() => setPresetsOpen((open) => !open)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', minWidth: 0, flex: 1 }}
          >
            <SectionIcon>
              <PresetsIcon fontSize="small" />
            </SectionIcon>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                {b.presetsTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {loadingPresets ? b.loading : formatMessage(b.presetCount, { n: presets.length })}
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{
                color: '#bf5f82',
                transition: 'transform 0.2s',
                transform: presetsOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </Box>
          <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
            <Button startIcon={<RefreshIcon />} onClick={() => loadPresets()}>
              {b.reload}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{ bgcolor: '#ff8ba7', '&:hover': { bgcolor: '#bf5f82' } }}
            >
              {b.createNew}
            </Button>
          </Stack>
        </Stack>

        <Collapse in={presetsOpen}>
          <Box sx={{ mt: 2 }}>
            {loadingPresets ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : presets.length === 0 ? (
              <Alert severity="info">{b.noPresets}</Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  gap: 2,
                }}
              >
                {presets.map((preset) => (
                  <Card key={preset.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    {preset.imageUrl ? (
                      <CardMedia
                        component="img"
                        height="160"
                        image={rewriteToCdn(preset.imageUrl)}
                        alt={preset.name}
                        onClick={() => openEditDialog(preset)}
                        sx={{ objectFit: 'cover', cursor: 'pointer' }}
                      />
                    ) : (
                      <Box
                        onClick={() => openEditDialog(preset)}
                        sx={{
                          height: 160,
                          bgcolor: alpha('#ff8ba7', 0.08),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <AiIcon sx={{ fontSize: 42, color: '#ff8ba7' }} />
                      </Box>
                    )}
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight={700} noWrap sx={{ pr: 1 }}>
                          {preset.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => openEditDialog(preset)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deletingId === preset.id}
                            onClick={() => handleDelete(preset)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>}

      <Dialog
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        fullScreen
        TransitionProps={{ onEntered: scrollUsageChartToToday }}
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            borderRadius: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ ...dialogTitleSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1.5 }}>
          {b.statsTitle}
          <IconButton onClick={() => setStatsOpen(false)} sx={{ color: '#e06a88' }} aria-label={b.cancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0 }}>
            <Box
              sx={{
                position: 'relative',
                flexShrink: 0,
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid #f0c9d6',
                backgroundColor: '#fff',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, minWidth: 0 }}>
                <TextField
                  select
                  size="small"
                  value={statsRange}
                  onChange={(event) => {
                    setStatsRange(event.target.value as UsageChartRange);
                    setChartFocus(null);
                  }}
                  sx={{ minWidth: 148, flexShrink: 0, ...chartFilterFieldSx }}
                >
                  {USAGE_CHART_RANGES.map((range) => (
                    <MenuItem key={range.id} value={range.id}>{b[range.labelKey]}</MenuItem>
                  ))}
                </TextField>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, ml: 'auto' }}>
                {(allUsageCostLoading || usageCostPoints != null) && (
                  <Chip
                    size="small"
                    label={allUsageCostLoading
                      ? b.usageCostCalculating
                      : formatMessage(b.usageCostTotal, { amount: Number(usageChartTotal.toFixed(4)) })}
                    sx={{ height: 26, fontWeight: 800, color: '#bf5f82', bgcolor: alpha('#ff8ba7', 0.16), flexShrink: 0 }}
                  />
                )}
              </Box>
              </Stack>
              <Autocomplete
                multiple
                size="small"
                options={[...users].sort((a, c) => (a.username || a.email).localeCompare(c.username || c.email))}
                value={users.filter((user) => chartAccountIds.includes(user._id))}
                onChange={(_, value) => setChartAccountIds(value.map((user) => user._id))}
                getOptionLabel={(user) => user.username || user.email}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                limitTags={compactAccounts ? 2 : 6}
                componentsProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                      border: '1px solid #f0c9d6',
                      boxShadow: '0 10px 28px rgba(80, 40, 55, 0.08)',
                      backgroundColor: '#fff',
                      backgroundImage: 'none',
                      '& .MuiAutocomplete-listbox': { p: 0.5 },
                      '& .MuiAutocomplete-option': { borderRadius: 1.5, alignItems: 'center', gap: 1.25 },
                      '& .MuiAutocomplete-option.Mui-focused': { bgcolor: '#fff6f9' },
                      '& .MuiAutocomplete-option[aria-selected="true"]': { bgcolor: '#ffeef4' },
                    },
                  },
                }}
                renderOption={(props, user) => {
                  const label = user.username || user.email || '?';
                  return (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        src={user.avatarUrl ? rewriteToCdn(user.avatarUrl) : undefined}
                        sx={{ width: 28, height: 28, fontSize: 13, bgcolor: `hsl(${accountHue(label)}, 62%, 72%)` }}
                      >
                        {label.trim().charAt(0).toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, color: '#4a2a36' }}>{label}</Typography>
                        {user.username && user.email ? (
                          <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.3, color: '#a06a7c' }}>{user.email}</Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                }}
                renderTags={(value, getTagProps) => value.map((user, index) => {
                  const label = user.username || user.email || '?';
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={user._id}
                      size="small"
                      avatar={(
                        <Avatar src={user.avatarUrl ? rewriteToCdn(user.avatarUrl) : undefined}>
                          {label.trim().charAt(0).toUpperCase() || '?'}
                        </Avatar>
                      )}
                      label={label}
                      sx={{ bgcolor: '#fff', border: '1px solid #f0c9d6', color: '#5c3344', fontWeight: 700 }}
                    />
                  );
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={b.usageChartAccounts}
                    placeholder={chartAccountIds.length ? '' : b.usageChartAllAccounts}
                  />
                )}
                sx={{ mb: 1.5, ...chartFilterFieldSx }}
              />
              {!allUsageCostLoading && statsCostFailed && usageCostPoints == null && (
                <Alert severity="warning">{b.usageCostRateLimit}</Alert>
              )}
              {!allUsageCostLoading && usageCostPoints != null && usageChartPeak <= 0 && (
                <Alert severity="info">{b.usageSubjectMoneyEmpty}</Alert>
              )}
              {usageChartPeak > 0 && (
                <Stack spacing={1.25}>
                  <Typography variant="caption" color="text.secondary">{b.usageSubjectDayHint}</Typography>
                  <Box ref={usageChartScrollRef} sx={{ flexShrink: 0, overflowX: 'auto', pb: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ width: 'max-content', minWidth: '100%' }}>
                      {(usageChart?.buckets || []).map((bucket) => {
                        const selected = chartFocus === bucket.t;
                        const dayMax = usageChartPeak > 0 ? usageChartPeak : 1;
                        return (
                          <Stack
                            key={bucket.t}
                            alignItems="center"
                            onClick={() => {
                              if (bucket.cost <= 0) return;
                              setChartFocus(selected ? null : bucket.t);
                            }}
                            sx={{
                              width: 76,
                              flexShrink: 0,
                              cursor: bucket.cost > 0 ? 'pointer' : 'default',
                              borderRadius: 1.5,
                              px: 0.25,
                              py: 0.5,
                              bgcolor: selected ? alpha('#ff8ba7', 0.18) : 'transparent',
                            }}
                          >
                            <Typography fontWeight={800} sx={{ color: '#9d174d', lineHeight: 1.2, fontSize: 12 }}>
                              {bucket.cost > 0 ? `$${Number(bucket.cost.toFixed(bucket.cost >= 1 ? 2 : 4))}` : ''}
                            </Typography>
                            <Box
                              sx={{
                                mt: 0.5,
                                width: 28,
                                height: Math.max(6, (bucket.cost / dayMax) * 220),
                                borderRadius: 1.25,
                                bgcolor: bucket.cost > 0 ? '#f48fb1' : alpha('#ff8ba7', 0.18),
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.1 }}>
                              {usageChartLabel(bucket.t, Boolean(usageChart?.hourly))}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                  {chartFocusBucket && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={800} color="#9d174d">
                        {usageChartLabel(chartFocusBucket.t, Boolean(usageChart?.hourly))}
                      </Typography>
                      <Button size="small" onClick={() => setChartFocus(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#bf5f82' }}>
                        {b.usageSubjectWholeRange}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
              {allUsageCostLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 3,
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha('#fff8fb', 0.78),
                  }}
                >
                  <CircularProgress size={36} thickness={4} sx={{ color: '#f48fb1' }} />
                </Box>
              )}
            </Box>
            {usageChartPeak > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid #f0c9d6',
                  p: 1.5,
                }}
              >
                <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflowX: 'hidden', overflowY: 'auto', overscrollBehavior: 'contain', pr: 0.5 }}>
                  {chartRows.map((row) => (
                    <Stack key={row.userId} spacing={0.35}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={row.avatarUrl ? rewriteToCdn(row.avatarUrl) : undefined}
                          sx={{ width: 28, height: 28, fontSize: 13, bgcolor: usageStackColor(row.userId, usageChart?.accounts || []) }}
                        >
                          {(row.name || '?').trim().charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{row.name}</Typography>
                        <Typography fontWeight={800} color="#9d174d" sx={{ flexShrink: 0 }}>
                          ${Number(row.count.toFixed(row.count >= 1 ? 2 : 4))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right', flexShrink: 0 }}>
                          {chartRowTotal > 0 ? `${Math.round((row.count / chartRowTotal) * 100)}%` : ''}
                        </Typography>
                      </Stack>
                      <Box sx={{ height: 8, borderRadius: 99, bgcolor: alpha('#ff8ba7', 0.12), overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${(row.count / chartRowMax) * 100}%`,
                            height: '100%',
                            borderRadius: 99,
                            bgcolor: usageStackColor(row.userId, usageChart?.accounts || []),
                          }}
                        />
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Paper
        sx={{
          ...gradientPanelSx,
          ...(openPanel === 'accounts'
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        <Box
          onClick={() => togglePanel('accounts')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: '#fff',
              background: 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)',
              boxShadow: '0 6px 14px rgba(191, 95, 130, 0.28)',
            }}
          >
            <AccountsIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {b.accountsTitle}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              color: '#e06a88',
              transition: 'transform 0.2s',
              transform: openPanel === 'accounts' ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
        <Collapse
          in={openPanel === 'accounts'}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '&.MuiCollapse-entered': {
              height: 'auto !important',
              flex: 1,
              minHeight: 0,
            },
            '& .MuiCollapse-wrapper': {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            },
            '& .MuiCollapse-wrapperInner': {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={b.searchUsers}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    background: 'linear-gradient(180deg, #ffffff 0%, #fff8fb 100%)',
                    '& fieldset': { borderColor: alpha('#f48fb1', 0.55) },
                    '&:hover fieldset': { borderColor: '#f48fb1' },
                    '&.Mui-focused fieldset': { borderColor: '#e06a88', borderWidth: 1.5 },
                  },
                }}
              />
              <Button
                onClick={() => setStatsOpen(true)}
                startIcon={<StatsIcon />}
                sx={{
                  flexShrink: 0,
                  height: 40,
                  px: { xs: 1.25, sm: 1.75 },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  color: '#bf5f82',
                  bgcolor: alpha('#ff8ba7', 0.16),
                  '&:hover': { bgcolor: alpha('#ff8ba7', 0.28) },
                }}
              >
                {b.statsTitle}
              </Button>
            </Stack>
            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : users.length === 0 ? (
              <Alert severity="info">{b.noUsers}</Alert>
            ) : (
              <Stack
                spacing={1}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  pr: 0.5,
                }}
              >
                {users.map((user) => {
                  const label = user.username || user.email || '?';
                  const initial = label.trim().charAt(0).toUpperCase() || '?';
                  const hue = accountHue(label);
                  const avatar = user.avatarUrl ? rewriteToCdn(user.avatarUrl) : '';
                  const settingsButton = (
                    <IconButton
                      size="small"
                      onClick={() => setSettingsUser(user)}
                      sx={{
                        flexShrink: 0,
                        color: '#e06a88',
                        backgroundImage: 'linear-gradient(135deg, rgba(255,193,212,0.7), rgba(255,139,167,0.35))',
                        '&:hover': {
                          color: '#fff',
                          backgroundImage: pinkFill,
                        },
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  );
                  const toggleControl = (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                      {togglingUserId === user._id && <CircularProgress size={16} sx={{ color: '#e06a88' }} />}
                      <Switch
                        checked={user.backgroundAiEnabled}
                        onChange={() => handleToggleUser(user)}
                        disabled={togglingUserId === user._id}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundImage: 'linear-gradient(90deg, #ffc1d4, #ff8ba7)',
                            opacity: 1,
                          },
                        }}
                      />
                    </Stack>
                  );
                  return (
                    <Box
                      key={user._id}
                      sx={{
                        display: 'flex',
                        flexDirection: compactAccounts ? 'column' : 'row',
                        alignItems: compactAccounts ? 'stretch' : 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2.5,
                        border: '1px solid transparent',
                        background: user.backgroundAiEnabled
                          ? 'linear-gradient(145deg, #fff7fb 0%, #ffffff 48%, #fff0f4 100%) padding-box, linear-gradient(135deg, #ffc1d4, #ff8ba7, #f48fb1) border-box'
                          : 'linear-gradient(145deg, #ffffff 0%, #fff8fb 100%) padding-box, linear-gradient(135deg, #ffd6e2, #f8bbd0) border-box',
                        boxShadow: user.backgroundAiEnabled
                          ? '0 10px 24px rgba(191, 95, 130, 0.10)'
                          : '0 4px 14px rgba(191, 95, 130, 0.04)',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: compactAccounts ? undefined : 1 }}>
                        <Box
                          sx={{
                            p: '2px',
                            borderRadius: '50%',
                            flexShrink: 0,
                            background: pinkFill,
                            boxShadow: '0 4px 10px rgba(191, 95, 130, 0.22)',
                          }}
                        >
                          <Avatar
                            src={avatar || undefined}
                            alt={label}
                            sx={{
                              width: 40,
                              height: 40,
                              fontWeight: 700,
                              border: '2px solid #fff',
                              bgcolor: `hsl(${hue} 62% 46%)`,
                              color: '#fff',
                            }}
                          >
                            {initial}
                          </Avatar>
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography fontWeight={700} noWrap>
                            {user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {user.email}
                          </Typography>
                        </Box>
                        {compactAccounts && settingsButton}
                        {compactAccounts && toggleControl}
                      </Stack>
                      <Box
                        sx={{
                          display: compactAccounts ? 'grid' : 'flex',
                          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                          alignItems: compactAccounts ? 'stretch' : 'center',
                          gap: 1,
                          flexShrink: 0,
                          minWidth: 0,
                        }}
                      >
                        {!compactAccounts && settingsButton}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setThemeUser(user)}
                          sx={accountActionButtonSx(compactAccounts)}
                        >
                          {b.themeList}
                        </Button>
                        <Badge
                          badgeContent={user.generationCount || 0}
                          showZero
                          overlap="rectangular"
                          sx={{
                            width: compactAccounts ? '100%' : 'auto',
                            minWidth: compactAccounts ? 0 : 'auto',
                            display: 'flex',
                            '& .MuiBadge-badge': {
                              background: pinkFill,
                              color: '#fff',
                              fontWeight: 700,
                              minWidth: 18,
                              height: 18,
                              fontSize: 11,
                              top: 2,
                              right: 4,
                              boxShadow: '0 2px 6px rgba(191, 95, 130, 0.35)',
                            },
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => void openUsage(user)}
                            sx={accountActionButtonSx(compactAccounts)}
                          >
                            {b.usageHistory}
                          </Button>
                        </Badge>
                        {!compactAccounts && toggleControl}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          resetDialog();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingPreset ? b.editPreset : b.createPreset}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={b.presetName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label={b.prompt}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              helperText={b.promptHelper}
            />
            <Button variant="outlined" component="label">
              {imageFile ? imageFile.name : b.chooseImage}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setImageFile(file);
                  setImagePreview(file ? URL.createObjectURL(file) : '');
                }}
              />
            </Button>
            {imagePreview && (
              <Box
                component="img"
                src={imagePreview}
                alt="preview"
                sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 2 }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              resetDialog();
            }}
            disabled={saving}
          >
            {b.cancel}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? b.saving : editingPreset ? b.save : b.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(usageUser)}
        onClose={() => {
          usageRequestRef.current += 1;
          setUsageUser(null);
          setOpenUsageCode(null);
          setUsagePages({});
          setUsageSearch('');
          setUsageProduct('all');
          setUsageCostsLoading(false);
          setUsageRequestCosts({});
        }}
        fullScreen
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            borderRadius: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={dialogTitleSx}>
          {formatMessage(b.usageTitle, { name: usageUser?.username || usageUser?.email || '' })}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {!loadingUsage && usageMachines.length > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 1.5, minWidth: 0, flexShrink: 0 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={b.usageSearch}
                  value={usageSearch}
                  onChange={(e) => setUsageSearch(e.target.value)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
                  }}
                />
                <Button
                  onClick={openSubjectStats}
                  startIcon={<StatsIcon />}
                  sx={{
                    flexShrink: 0,
                    height: 40,
                    px: { xs: 1.25, sm: 1.75 },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    color: '#bf5f82',
                    bgcolor: alpha('#ff8ba7', 0.16),
                    '&:hover': { bgcolor: alpha('#ff8ba7', 0.28) },
                  }}
                >
                  {b.usageSubjectStats}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                <ToggleButtonGroup
                  exclusive
                  value={usageProduct}
                  onChange={(_, value: 'all' | 'lite' | 'pro' | null) => {
                    if (value) setUsageProduct(value);
                  }}
                  sx={{
                    flex: { xs: 1, sm: '0 0 auto' },
                    minWidth: 0,
                    height: 40,
                    bgcolor: '#fff',
                    '& .MuiToggleButton-root': {
                      flex: { xs: 1, sm: 'none' },
                      height: 40,
                      px: { xs: 0.75, sm: 1.75 },
                      minWidth: { xs: 0, sm: 76 },
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontWeight: 700,
                      lineHeight: 1,
                      borderColor: alpha('#ff8ba7', 0.2),
                    },
                    '& .Mui-selected': {
                      color: '#bf5f82 !important',
                      bgcolor: `${alpha('#ff8ba7', 0.14)} !important`,
                    },
                  }}
                >
                  <ToggleButton value="all">{b.usageFilterAll}</ToggleButton>
                  <ToggleButton value="lite">Lite</ToggleButton>
                  <ToggleButton value="pro">Pro</ToggleButton>
                </ToggleButtonGroup>
                {(usageCostsLoading || showUsageCost) && (
                  <Chip
                    label={usageCostsLoading
                      ? b.usageCostCalculating
                      : formatMessage(b.usageCostTotal, { amount: Number(usageCostTotal.toFixed(4)) })}
                    sx={{ flexShrink: 0, height: 40, fontWeight: 800, color: '#bf5f82', bgcolor: alpha('#ff8ba7', 0.16) }}
                  />
                )}
              </Stack>
            </Stack>
          )}
          {loadingUsage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : usageMachines.length === 0 ? (
            <Alert severity="info">{b.usageEmpty}</Alert>
          ) : visibleUsageMachines.length === 0 ? (
            <Alert severity="info">{b.usageNoMatch}</Alert>
          ) : (
            <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', pr: 0.5 }}>
              {visibleUsageMachines.map((machine) => {
                const open = openUsageCode === machine.machineCode;
                const page = usagePages[machine.machineCode];
                const items = page?.items || [];
                const product = String(machine.appProduct || '').trim().toLowerCase();
                const productColor =
                  product === 'lite' ? '#0284c7' : product === 'pro' ? '#c2410c' : product === 'mono' ? '#4b5563' : '';
                return (
                  <Accordion
                    key={machine.machineCode}
                    expanded={open}
                    onChange={(_, isExpanded) => {
                      if (!isExpanded) {
                        setOpenUsageCode(null);
                        return;
                      }
                      setOpenUsageCode(machine.machineCode);
                      if (!usagePages[machine.machineCode]?.items.length) {
                        void loadUsagePage(usageUser?._id || '', machine.machineCode, 0);
                      }
                    }}
                    disableGutters
                    elevation={0}
                    TransitionProps={{ unmountOnExit: true }}
                    sx={{
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: open ? alpha('#ff8ba7', 0.45) : alpha('#ff8ba7', 0.16),
                      borderRadius: '14px !important',
                      overflow: open ? 'visible' : 'hidden',
                      bgcolor: '#fff',
                      boxShadow: open ? '0 8px 22px rgba(191, 95, 130, 0.08)' : 'none',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#ff8ba7' }} />}
                      sx={{
                        position: open ? 'sticky' : 'static',
                        top: 0,
                        zIndex: 2,
                        bgcolor: '#fff',
                        borderBottom: open ? '1px solid' : 'none',
                        borderColor: alpha('#ff8ba7', 0.2),
                        '& .MuiAccordionSummary-content': { my: 1.25, width: '100%' },
                      }}
                    >
                      <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0, pr: 0.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                          {productColor && (
                            <Chip
                              size="small"
                              label={product.toUpperCase()}
                              sx={{
                                flexShrink: 0,
                                width: 58,
                                height: 22,
                                fontWeight: 800,
                                letterSpacing: 0.4,
                                color: productColor,
                                bgcolor: alpha(productColor, 0.12),
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                          )}
                          <Typography fontWeight={800} noWrap sx={{ flex: 1, minWidth: 0, color: '#2a2140' }}>
                            {machine.machineName
                              || (product
                                ? formatMessage(b.usageUnnamed, {
                                    kind: product.charAt(0).toUpperCase() + product.slice(1),
                                  })
                                : machine.machineCode)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, pl: productColor ? '66px' : 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              color: '#ff8ba7',
                              fontWeight: 700,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            }}
                          >
                            {machine.machineCode}
                          </Typography>
                          <Chip
                            size="small"
                            label={formatMessage(b.usageShowing, {
                              n: machine.total,
                            })}
                            sx={{
                              flexShrink: 0,
                              height: 22,
                              fontWeight: 700,
                              color: '#bf5f82',
                              bgcolor: alpha('#ff8ba7', 0.12),
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {page?.loading && items.length === 0 ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={22} />
                          </Box>
                        ) : null}
                        {items.map((item, index) => (
                          <Stack
                            key={`${machine.machineCode}-${item.createdAt || index}-${index}`}
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: alpha('#ff8ba7', 0.14),
                              bgcolor: '#fcfbff',
                            }}
                          >
                            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                              {(item.resultUrls?.length ? item.resultUrls : [item.resultUrl])
                                .filter(Boolean)
                                .slice(0, 4)
                                .map((url) => (
                                  <Box
                                    key={url}
                                    component="img"
                                    src={url}
                                    alt=""
                                    sx={{
                                      width: 72,
                                      height: 72,
                                      objectFit: 'cover',
                                      borderRadius: 1.5,
                                      bgcolor: 'action.hover',
                                    }}
                                  />
                                ))}
                            </Stack>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                {(item.imageCount || item.resultUrls?.length || 1) > 1 && (
                                  <Chip
                                    size="small"
                                    label={formatMessage(b.multiSlotImages, {
                                      n: item.imageCount || item.resultUrls?.length || 1,
                                    })}
                                    sx={{
                                      fontWeight: 700,
                                      color: '#c2410c',
                                      bgcolor: alpha('#c2410c', 0.12),
                                    }}
                                  />
                                )}
                                <Chip size="small" label={qualityLabel(item.quality)} />
                                {item.imageSize && (
                                  <Chip size="small" variant="outlined" label={item.imageSize} />
                                )}
                                {generationCost(item) != null && (
                                  <Chip size="small" label={`$${generationCost(item)}`} sx={{ fontWeight: 700 }} />
                                )}
                              </Stack>
                              {(usageHistoryName(item.themeLabels, item.themeName, lang) || usageHistoryName(item.presetLabels, item.presetName, lang)) ? (
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#9d174d', lineHeight: 1.35 }}>
                                  {usageHistoryName(item.themeLabels, item.themeName, lang)
                                    ? `${b.usageTheme}: ${usageHistoryName(item.themeLabels, item.themeName, lang)}`
                                    : ''}
                                  {usageHistoryName(item.themeLabels, item.themeName, lang) && usageHistoryName(item.presetLabels, item.presetName, lang) ? ' · ' : ''}
                                  {usageHistoryName(item.presetLabels, item.presetName, lang)
                                    ? `${b.usageBackground}: ${usageHistoryName(item.presetLabels, item.presetName, lang)}`
                                    : ''}
                                </Typography>
                              ) : null}
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="a"
                                href={item.resultUrl}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ display: 'block', color: 'primary.main' }}
                                noWrap
                              >
                                {item.resultUrl}
                              </Typography>
                            </Box>
                          </Stack>
                        ))}
                        {items.length < machine.total && (
                          <Button
                            size="small"
                            disabled={page?.loading}
                            onClick={() => {
                              if (!usageUser) return;
                              void loadUsagePage(usageUser._id, machine.machineCode, items.length);
                            }}
                          >
                            {page?.loading ? b.loading : b.usageLoadMore}
                          </Button>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setUsageUser(null)} sx={dialogCancelSx}>
            {b.cancel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={subjectOpen}
        onClose={() => setSubjectOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            borderRadius: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ ...dialogTitleSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1.5 }}>
          {b.usageSubjectStats}
          <IconButton onClick={() => setSubjectOpen(false)} sx={{ color: '#e06a88' }} aria-label={b.cancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                select
                size="small"
                value={subjectRange || '7d'}
                onChange={(event) => {
                  const range = event.target.value as UsageChartRange;
                  const bounds = chartPresetBounds(range);
                  setSubjectRange(range);
                  setSubjectFrom(bounds.from);
                  setSubjectTo(bounds.to);
                  setSubjectFocus(null);
                }}
                sx={{ width: { xs: 128, sm: 160 }, flexShrink: 0, ...chartFilterFieldSx }}
              >
                {USAGE_CHART_RANGES.map((range) => (
                  <MenuItem key={range.id} value={range.id}>{b[range.labelKey]}</MenuItem>
                ))}
              </TextField>
              <ToggleButtonGroup
                exclusive
                value={subjectTab === 'money' ? 'money' : subjectKind}
                onChange={(_, value: 'money' | 'theme' | 'preset' | null) => {
                  if (!value) return;
                  setSubjectFocus(null);
                  if (value === 'money') {
                    setSubjectTab('money');
                    return;
                  }
                  setSubjectTab('usage');
                  setSubjectKind(value);
                }}
                sx={{
                  ml: 'auto',
                  height: 40,
                  flexShrink: 0,
                  bgcolor: '#fff',
                  '& .MuiToggleButton-root': {
                    height: 40,
                    px: { xs: 1, sm: 1.75 },
                    textTransform: 'none',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    borderColor: alpha('#ff8ba7', 0.2),
                  },
                  '& .Mui-selected': {
                    color: '#fff !important',
                    bgcolor: '#f48fb1 !important',
                  },
                }}
              >
                <ToggleButton value="money">{b.usageSubjectTabUse}</ToggleButton>
                <ToggleButton value="theme">{b.usageTheme}</ToggleButton>
                <ToggleButton value="preset">{b.usageBackground}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {subjectTab === 'usage' && (
            <>
            <Box sx={{ flexShrink: 0, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f0c9d6', p: 1.25 }}>
              {subjectLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress size={32} sx={{ color: '#f48fb1' }} />
                </Box>
              ) : subjectPeak <= 0 ? (
                <Alert severity="info">{b.usageSubjectEmpty}</Alert>
              ) : (
                <Stack spacing={1.25}>
                  <Typography variant="caption" color="text.secondary">{b.usageSubjectDayHint}</Typography>
                  <Box ref={subjectScrollRef} sx={{ overflowX: 'auto', pb: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ width: 'max-content', minWidth: '100%' }}>
                      {(subjectChart?.buckets || []).map((bucket) => {
                        const selected = subjectFocus === bucket.t;
                        const dayMax = subjectPeak > 0 ? subjectPeak : 1;
                        return (
                          <Stack
                            key={bucket.t}
                            alignItems="center"
                            onClick={() => {
                              if (bucket.cost <= 0) return;
                              setSubjectFocus(selected ? null : bucket.t);
                            }}
                            sx={{
                              width: 64,
                              flexShrink: 0,
                              cursor: bucket.cost > 0 ? 'pointer' : 'default',
                              borderRadius: 1.5,
                              px: 0.25,
                              py: 0.5,
                              bgcolor: selected ? alpha('#ff8ba7', 0.18) : 'transparent',
                            }}
                          >
                            <Typography fontWeight={800} sx={{ color: '#9d174d', lineHeight: 1.2, fontSize: 15 }}>
                              {bucket.cost > 0 ? Math.round(bucket.cost) : ''}
                            </Typography>
                            <Box
                              sx={{
                                mt: 0.5,
                                width: 28,
                                height: Math.max(6, (bucket.cost / dayMax) * 220),
                                borderRadius: 1.25,
                                bgcolor: bucket.cost > 0 ? '#f48fb1' : alpha('#ff8ba7', 0.18),
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.1 }}>
                              {usageChartLabel(bucket.t, Boolean(subjectChart?.hourly))}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                  {subjectFocusBucket && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={800} color="#9d174d">
                        {usageChartLabel(subjectFocusBucket.t, Boolean(subjectChart?.hourly))}
                      </Typography>
                      <Button size="small" onClick={() => setSubjectFocus(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#bf5f82' }}>
                        {b.usageSubjectWholeRange}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
            {!subjectLoading && subjectPeak > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRadius: 2,
                  bgcolor: '#fff',
                  border: '1px solid #f0c9d6',
                  p: 1.25,
                }}
              >
                <Stack
                  spacing={1}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    pr: 0.5,
                  }}
                >
                  {subjectRows.map((row) => (
                    <Stack key={row.userId} spacing={0.35}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, bgcolor: usageStackColor(row.userId, subjectChart?.accounts || []) }} />
                        <Typography fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{row.name}</Typography>
                        <Typography fontWeight={800} color="#9d174d" sx={{ flexShrink: 0 }}>
                          {Math.round(row.count)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right', flexShrink: 0 }}>
                          {subjectRowTotal > 0 ? `${Math.round((row.count / subjectRowTotal) * 100)}%` : ''}
                        </Typography>
                      </Stack>
                      <Box sx={{ height: 8, borderRadius: 99, bgcolor: alpha('#ff8ba7', 0.12), overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${(row.count / subjectRowMax) * 100}%`,
                            height: '100%',
                            borderRadius: 99,
                            bgcolor: usageStackColor(row.userId, subjectChart?.accounts || []),
                          }}
                        />
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            </>
            )}
            {subjectTab === 'money' && (
            <>
            <Box sx={{ flexShrink: 0, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f0c9d6', p: 1.25 }}>
              {usageCostsLoading && accountMoneyPoints == null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress size={32} sx={{ color: '#f48fb1' }} />
                </Box>
              ) : moneyPeak <= 0 ? (
                <Alert severity="info">{b.usageSubjectMoneyEmpty}</Alert>
              ) : (
                <Stack spacing={1.25}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" color="text.secondary">{b.usageSubjectDayHint}</Typography>
                    <Chip
                      size="small"
                      label={formatMessage(b.usageCostTotal, { amount: Number(moneyRowTotal.toFixed(4)) })}
                      sx={{ height: 28, fontWeight: 800, color: '#bf5f82', bgcolor: alpha('#ff8ba7', 0.16) }}
                    />
                  </Stack>
                  <Box ref={moneyScrollRef} sx={{ overflowX: 'auto', pb: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ width: 'max-content', minWidth: '100%' }}>
                      {(moneyChart?.buckets || []).map((bucket) => {
                        const selected = subjectFocus === bucket.t;
                        const dayMax = moneyPeak > 0 ? moneyPeak : 1;
                        const moneyLabel = bucket.cost > 0
                          ? `$${Number(bucket.cost.toFixed(bucket.cost >= 1 ? 2 : 4))}`
                          : '';
                        return (
                          <Stack
                            key={bucket.t}
                            alignItems="center"
                            onClick={() => {
                              if (bucket.cost <= 0) return;
                              setSubjectFocus(selected ? null : bucket.t);
                            }}
                            sx={{
                              width: 76,
                              flexShrink: 0,
                              cursor: bucket.cost > 0 ? 'pointer' : 'default',
                              borderRadius: 1.5,
                              px: 0.25,
                              py: 0.5,
                              bgcolor: selected ? alpha('#ff8ba7', 0.18) : 'transparent',
                            }}
                          >
                            <Typography fontWeight={800} sx={{ color: '#9d174d', lineHeight: 1.2, fontSize: 12 }}>
                              {moneyLabel}
                            </Typography>
                            <Box
                              sx={{
                                mt: 0.5,
                                width: 28,
                                height: Math.max(6, (bucket.cost / dayMax) * 220),
                                borderRadius: 1.25,
                                bgcolor: bucket.cost > 0 ? '#f48fb1' : alpha('#ff8ba7', 0.18),
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.1 }}>
                              {usageChartLabel(bucket.t, Boolean(moneyChart?.hourly))}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                  {moneyFocusBucket && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={800} color="#9d174d">
                        {usageChartLabel(moneyFocusBucket.t, Boolean(moneyChart?.hourly))}
                      </Typography>
                      <Button size="small" onClick={() => setSubjectFocus(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#bf5f82' }}>
                        {b.usageSubjectWholeRange}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
            {!(usageCostsLoading && accountMoneyPoints == null) && moneyPeak > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRadius: 2,
                  bgcolor: '#fff',
                  border: '1px solid #f0c9d6',
                  p: 1.25,
                }}
              >
                <Stack
                  spacing={1}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    pr: 0.5,
                  }}
                >
                  {moneyRows.map((row) => (
                    <Stack key={row.userId} spacing={0.35}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, bgcolor: usageStackColor(row.userId, moneyChart?.accounts || []) }} />
                        <Typography fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{row.name}</Typography>
                        <Typography fontWeight={800} color="#9d174d" sx={{ flexShrink: 0 }}>
                          ${Number(row.count.toFixed(row.count >= 1 ? 2 : 4))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right', flexShrink: 0 }}>
                          {moneyRowTotal > 0 ? `${Math.round((row.count / moneyRowTotal) * 100)}%` : ''}
                        </Typography>
                      </Stack>
                      <Box sx={{ height: 8, borderRadius: 99, bgcolor: alpha('#ff8ba7', 0.12), overflow: 'hidden' }}>
                        <Box
                          sx={{
                            width: `${(row.count / moneyRowMax) * 100}%`,
                            height: '100%',
                            borderRadius: 99,
                            bgcolor: usageStackColor(row.userId, moneyChart?.accounts || []),
                          }}
                        />
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(settingsUser)}
        onClose={() => !savingMultiSlot && !savingAccountGen && setSettingsUser(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>
          {settingsUser?.username || settingsUser?.email}
        </DialogTitle>
        <DialogContent sx={{ pt: '18px !important' }}>
          <Stack spacing={1.5}>
            <Box sx={settingsCardSx}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={800} color="#bf5f82">{b.multiSlotTitle}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                    {b.multiSlotHint}
                  </Typography>
                </Box>
                <Switch
                  checked={settingsUser?.backgroundAiMultiSlotEnabled === true}
                  onChange={() => settingsUser && void handleToggleMultiSlot(settingsUser)}
                  disabled={savingMultiSlot}
                  sx={pinkSwitchSx}
                />
              </Stack>
            </Box>
            <Box sx={settingsCardSx}>
              <Typography fontWeight={800} color="#bf5f82">{b.quotaTitle}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, mb: 1.25 }}>
                {b.quotaHint}
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={accountQuota}
                placeholder={b.quotaPlaceholder}
                onChange={(event) => {
                  const next = event.target.value.replace(/[^\d]/g, '');
                  setAccountQuota(next);
                }}
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        aria-label="decrease"
                        onClick={() => {
                          const current = accountQuota.trim() === '' ? 0 : Number(accountQuota);
                          setAccountQuota(String(Math.max(0, (Number.isFinite(current) ? current : 0) - 1)));
                        }}
                        sx={{ color: '#bf5f82' }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="increase"
                        onClick={() => {
                          const current = accountQuota.trim() === '' ? 0 : Number(accountQuota);
                          setAccountQuota(String((Number.isFinite(current) ? current : 0) + 1));
                        }}
                        sx={{ color: '#bf5f82' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...settingsFieldSx,
                  '& input': { textAlign: 'center', fontWeight: 800 },
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                <Chip
                  label={formatMessage(b.quotaUsed, { n: settingsUser?.quotaUsed || 0 })}
                  sx={{ fontWeight: 800, color: '#bf5f82', bgcolor: alpha('#ff8ba7', 0.16) }}
                />
                <Chip
                  label={settingsUser?.quotaLimit == null
                    ? b.quotaUnlimited
                    : formatMessage(b.quotaRemaining, { n: settingsUser?.quotaRemaining || 0 })}
                  sx={{ fontWeight: 800, color: '#fff', background: pinkFill }}
                />
              </Stack>
            </Box>
            <Box sx={settingsCardSx}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {b.accountGenHint}
              </Typography>
              <Typography variant="caption" fontWeight={800} color="#bf5f82" sx={{ letterSpacing: '0.04em' }}>
                {b.providerTitle}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={accountProvider}
                onChange={(_, value) => {
                  if (value) setAccountProvider(value);
                }}
                sx={{ ...pillToggleSx, mt: 0.75, mb: 1.5 }}
              >
                <ToggleButton value="fal">Fal</ToggleButton>
                <ToggleButton value="piapi">PiAPI</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" fontWeight={800} color="#bf5f82" sx={{ letterSpacing: '0.04em' }}>
                {b.quality}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={accountQuality}
                onChange={(_, value) => {
                  if (value) setAccountQuality(value);
                }}
                sx={{ ...pillToggleSx, mt: 0.75, mb: 1.5 }}
              >
                <ToggleButton value="low">{b.qualityLow}</ToggleButton>
                <ToggleButton value="medium">{b.qualityMedium}</ToggleButton>
                <ToggleButton value="high">{b.qualityHigh}</ToggleButton>
              </ToggleButtonGroup>
              {(() => {
                const picked = accountModels.find((model) => model.id === accountModel);
                const pickedLabel = picked
                  ? `${picked.name || picked.id}${picked.priceLabel ? ` · ${picked.priceLabel}` : ''}`
                  : b.chooseModel;
                return (
                  <Button
                    fullWidth
                    disabled={!accountModels.length}
                    onClick={() => setModelPickerOpen(true)}
                    sx={{
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      fontWeight: 700,
                      color: picked ? '#bf5f82' : 'text.secondary',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha('#ff8ba7', 0.55),
                      bgcolor: '#fff',
                      px: 1.5,
                      py: 1.1,
                      '&:hover': { borderColor: '#ff8ba7', bgcolor: '#fff8fb' },
                    }}
                  >
                    <Box component="span" sx={{ textAlign: 'left', minWidth: 0 }}>
                      {pickedLabel}
                    </Box>
                    <ExpandMoreIcon sx={{ color: '#e06a88' }} />
                  </Button>
                );
              })()}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button
            onClick={() => setSettingsUser(null)}
            disabled={savingMultiSlot || savingAccountGen}
            sx={dialogCancelSx}
          >
            {b.cancel}
          </Button>
          <Button
            variant="contained"
            disabled={savingAccountGen || !accountModel}
            onClick={() => void saveAccountGeneration()}
            sx={dialogSaveSx}
          >
            {savingAccountGen ? b.saving : b.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modelPickerOpen}
        onClose={() => setModelPickerOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ ...dialogTitleSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          {b.chooseModel}
          <IconButton aria-label={b.cancel} onClick={() => setModelPickerOpen(false)} sx={{ color: '#e06a88' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={0.75}>
            {accountModels.map((model) => {
              const selected = model.id === accountModel;
              return (
                <Button
                  key={model.id}
                  fullWidth
                  onClick={() => {
                    setAccountModel(model.id);
                    setModelPickerOpen(false);
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    fontWeight: selected ? 800 : 600,
                    color: selected ? '#fff' : '#bf5f82',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    bgcolor: selected ? '#ff8ba7' : '#fff',
                    border: '1px solid',
                    borderColor: alpha('#ff8ba7', selected ? 0 : 0.4),
                    '&:hover': { bgcolor: selected ? '#f48fb1' : '#fff5f8' },
                  }}
                >
                  {model.name || model.id}
                  {model.priceLabel ? ` · ${model.priceLabel}` : ''}
                </Button>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={multiSlotConfirmOpen}
        onClose={() => !savingMultiSlot && setMultiSlotConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#4a148c' }}>{b.multiSlotConfirmTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {b.multiSlotConfirm}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMultiSlotConfirmOpen(false)}
            disabled={savingMultiSlot}
            sx={{ color: '#bf5f82', fontWeight: 700 }}
          >
            {b.cancel}
          </Button>
          <Button
            variant="contained"
            disabled={savingMultiSlot || !settingsUser}
            onClick={() => {
              if (!settingsUser) return;
              setMultiSlotConfirmOpen(false);
              void saveMultiSlot(settingsUser, true);
            }}
            sx={{ fontWeight: 700, bgcolor: '#ff8ba7', '&:hover': { bgcolor: '#bf5f82' } }}
          >
            {b.multiSlotConfirmYes}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pwDialogOpen}
        onClose={() => !pwBusy && setPwDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{b.revealTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {b.revealBody}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={b.adminPassword}
            type="password"
            fullWidth
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), void confirmRevealPassword())}
            disabled={pwBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwDialogOpen(false)} disabled={pwBusy}>
            {b.cancel}
          </Button>
          <Button variant="contained" onClick={() => void confirmRevealPassword()} disabled={pwBusy}>
            {pwBusy ? <CircularProgress size={16} /> : b.view}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(themeUser)}
        onClose={() => {
          setThemeUser(null);
          setThemeDetailOpen(false);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>
          {themeUser ? `${b.themeDialog} — ${themeUser.username}` : b.themeDialog}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1.5, sm: 3 } }}>
          {themeUser ? (
            <UserAiThemes
              adminUserId={themeUser._id}
              username={themeUser.username}
              onActiveThemeChange={(theme) => setThemeDetailOpen(Boolean(theme))}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ ...dialogActionsSx, justifyContent: 'space-between' }}>
          {themeDetailOpen ? (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => window.dispatchEvent(new Event('ai-themes-back'))}
              sx={dialogCancelSx}
            >
              {b.back}
            </Button>
          ) : (
            <Box />
          )}
          <Button
            onClick={() => {
              setThemeUser(null);
              setThemeDetailOpen(false);
            }}
            sx={dialogCancelSx}
          >
            {b.cancel}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BackgroundAi;
